import * as React from 'react';
import type { TPAComponentsConfig, TPAComponentsProviderProps } from './types';
import { overrideExtendedPalette } from './constants';

export const TPAComponentsContext = React.createContext<TPAComponentsConfig>({
  mobile: false,
  rtl: false,
  seo: false,
  cssVars: false,
});

export const useTPAComponentsContext = () =>
  React.useContext(TPAComponentsContext);

const usePaletteContext = (isExtendedPaletteEnabled: boolean) => {
  const [isExtendedPaletteEnabledState, setIsExtendedPaletteEnabledState] =
    React.useState<boolean | undefined>(undefined);

  const parentContextValue = useTPAComponentsContext();

  React.useLayoutEffect(() => {
    setIsExtendedPaletteEnabledState(isExtendedPaletteEnabled);
  }, [isExtendedPaletteEnabled]);

  /**
   * This hook enables extended palette in parent context. This is needed in a case where an app is wrapped with TPAComponentsConfig by yoshi.
   */
  React.useEffect(() => {
    if (
      parentContextValue._setIsExtendedPaletteEnabledState &&
      !parentContextValue.isExtendedPaletteEnabled &&
      isExtendedPaletteEnabledState
    ) {
      parentContextValue._setIsExtendedPaletteEnabledState(true);
    }
  }, [
    isExtendedPaletteEnabledState,
    parentContextValue._setIsExtendedPaletteEnabledState,
  ]);

  return {
    paletteContextValue: {
      isExtendedPaletteEnabled: isExtendedPaletteEnabledState,
      _setIsExtendedPaletteEnabledState: setIsExtendedPaletteEnabledState,
    },
    style: isExtendedPaletteEnabledState ? {} : overrideExtendedPalette,
  };
};

export const TPAComponentsProvider = ({
  value,
  children,
  dataHook = 'tpa-components-provider',
  as: As = 'div',
}: TPAComponentsProviderProps) => {
  const { paletteContextValue, style } = usePaletteContext(
    value.isExtendedPaletteEnabled,
  );

  return (
    <As style={style} data-hook={dataHook}>
      <TPAComponentsContext.Provider
        value={{
          ...value,
          ...paletteContextValue,
        }}
      >
        {children}
      </TPAComponentsContext.Provider>
    </As>
  );
};

export const TPAComponentsConsumer = TPAComponentsContext.Consumer;
